// Google Analytics 4 Integration with Cookie Consent
// Only loads and tracks when user has given analytics consent

import { hasConsent } from '@/lib/cookieManager';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = 'G-XGM01928BF';

let isInitialized = false;

/**
 * Initialize Google Analytics only if consent is given
 * Note: GA4 is now loaded directly in index.html, this function adds consent-based enhancements
 */
export function initializeAnalytics(): void {
  if (isInitialized) {
    console.log('[Analytics] Already initialized, skipping');
    return;
  }
  if (!hasConsent('analytics')) {
    console.log('[Analytics] Consent not given, skipping initialization');
    return;
  }

  console.log('[Analytics] Starting initialization...');

  // Check if gtag is already loaded from index.html
  if (typeof window.gtag === 'function') {
    console.log('[Analytics] gtag already loaded from index.html');
    isInitialized = true;
    console.log('[Analytics] Enhanced tracking enabled with consent');
    return;
  }

  // Fallback: Load gtag script if not already present
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.onload = () => console.log('[Analytics] Script loaded successfully');
  script.onerror = () => console.error('[Analytics] Failed to load script');
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure',
    send_page_view: true,
  });

  isInitialized = true;
  console.log('[Analytics] Initialized successfully with ID:', GA_MEASUREMENT_ID);
}

/**
 * Track page view
 */
export function trackPageView(path: string, title?: string): void {
  if (!isInitialized || !hasConsent('analytics')) {
    console.log('[Analytics] Skipping page view - initialized:', isInitialized, 'consent:', hasConsent('analytics'));
    return;
  }

  console.log('[Analytics] Tracking page view:', path, title || document.title);
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });
}

/**
 * Track custom events
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
): void {
  if (!isInitialized || !hasConsent('analytics')) return;

  window.gtag('event', eventName, params);
}

// Pre-defined event tracking functions
export const analytics = {
  // User actions
  signUp: (method: string) => trackEvent('sign_up', { method }),
  signIn: (method: string) => trackEvent('login', { method }),
  
  // Mission events
  missionStart: (missionId: string, missionName: string) => 
    trackEvent('mission_start', { mission_id: missionId, mission_name: missionName }),
  missionComplete: (missionId: string, missionName: string) => 
    trackEvent('mission_complete', { mission_id: missionId, mission_name: missionName }),
  
  // Reading events
  readingGenerate: (readingType: string) => 
    trackEvent('reading_generate', { reading_type: readingType }),
  readingShare: (readingType: string) => 
    trackEvent('share', { content_type: 'reading', item_id: readingType }),
  
  // Daily rituals
  ritualUnlock: (ritualType: string) => 
    trackEvent('ritual_unlock', { ritual_type: ritualType }),
  ritualComplete: (ritualType: string) => 
    trackEvent('ritual_complete', { ritual_type: ritualType }),
  
  // Engagement
  streakAchieved: (streakDays: number) => 
    trackEvent('streak_achieved', { streak_days: streakDays }),
  badgeUnlock: (badgeId: string, badgeName: string) => 
    trackEvent('badge_unlock', { badge_id: badgeId, badge_name: badgeName }),
  
  // Purchases
  purchase: (itemId: string, value: number, currency: string = 'USD') => 
    trackEvent('purchase', { 
      transaction_id: `${Date.now()}`,
      value,
      currency,
      item_id: itemId
    }),
  
  // Course events
  courseStart: (courseId: string) => 
    trackEvent('course_start', { course_id: courseId }),
  courseComplete: (courseId: string) => 
    trackEvent('course_complete', { course_id: courseId }),
  
  // Referrals
  referralShare: (method: string) => 
    trackEvent('referral_share', { method }),
  referralSignup: () => 
    trackEvent('referral_signup'),
};

/**
 * Remove analytics when consent is revoked
 */
export function removeAnalytics(): void {
  if (!isInitialized) return;

  // Remove gtag script
  const scripts = document.querySelectorAll(`script[src*="googletagmanager.com"]`);
  scripts.forEach((script) => script.remove());

  // Clear cookies (GA cookies)
  const cookies = document.cookie.split(';');
  cookies.forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (name.startsWith('_ga') || name.startsWith('_gid')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });

  isInitialized = false;
  console.log('[Analytics] Removed');
}
